import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import LeadsService from '../../LeadMng/services/leadsService';
import {
  TrendingUp,
  Calendar,
  Award,
  FolderOpen,
  Building,
  GitBranch,
  Sparkles,
  Users,
  Target,
  BarChart3,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Rocket,
  Zap,
  Shield,
  Globe
} from 'lucide-react';

// Import sub-components
import OverviewDashboard from './OverviewDashboard';
import AttendanceDashboard from './AttendanceDashboard';
import TasksDashboard from './TasksDashboard';
import ProjectsDashboard from './ProjectsDashboard';
import BranchesDashboard from './BranchesDashboard';
import LeadsDashboard from './LeadsDashboard';

// API Base URL
const API_BASE_URL = 'https://crm-tkxn.onrender.com';

// Particle Background Component


// Animated Stat Card Component
const AnimatedStatCard = ({ icon, title, value, change, color, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 transform transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        } hover:scale-105 hover:shadow-xl hover:bg-white/90 transition-all duration-300`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${color} shadow-md`}>
          {icon}
        </div>
        <div className={`text-sm font-semibold px-2 py-1 rounded-full ${change >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          }`}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-gray-600 font-medium">{title}</p>
      <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full bg-gradient-to-r ${color} transition-all duration-1000 ease-out`}
          style={{ width: isVisible ? `${Math.min(100, (parseInt(value) || 0) * 100 / 50)}%` : '0%' }}
        />
      </div>
    </div>
  );
};

// Glowing Tab Navigation Component
const GlowingTab = ({ id, name, icon, isActive, onClick }) => {
  return (
    <button
      onClick={() => onClick(id)}
      className={`relative flex flex-col md:flex-row items-center justify-center md:space-x-2 gap-1.5 md:gap-0 py-3 px-1 sm:px-2 md:py-3 md:px-5 font-semibold text-[11px] sm:text-xs md:text-sm rounded-xl transition-all duration-300 transform w-full md:w-auto ${isActive
        ? 'text-white bg-gradient-to-r from-blue-500 to-purple-600 shadow-md shadow-blue-500/25 md:scale-105 border border-transparent'
        : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm hover:border-gray-300'
        }`}
    >
      <div className={`${isActive ? 'text-white' : 'text-gray-500'} scale-[0.85] md:scale-100`}>
        {icon}
      </div>
      <span className="tracking-wide whitespace-nowrap">{name}</span>
      {isActive && (
        <div className="hidden md:block absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-400 rounded-full animate-ping" />
      )}
    </button>
  );
};

// Loading Animation Component
const AnimatedLoading = () => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center relative z-10">
        <div className="relative">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto mb-6 animate-spin-slow flex items-center justify-center">
            <Rocket className="w-8 h-8 text-white transform -rotate-45" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-xl opacity-30 animate-pulse"></div>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Preparing Your Dashboard</h3>
        <p className="text-gray-600">Loading business intelligence data...</p>
        <div className="mt-4 flex justify-center space-x-1">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Error State with Animation
const AnimatedError = ({ error, onRetry }) => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 max-w-md w-full mx-4 text-center shadow-2xl border border-white/20 transform animate-fade-in-up">
        <div className="relative mb-6">
          <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-600 rounded-full mx-auto flex items-center justify-center shadow-lg">
            <AlertCircle className="w-10 h-10 text-white" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-600 rounded-full blur-xl opacity-20"></div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Dashboard Error</h2>
        <p className="text-gray-700 mb-6 leading-relaxed">{error}</p>
        <div className="space-y-3">
          <button
            onClick={onRetry}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Try Again
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('authToken');
              window.location.href = '/login';
            }}
            className="w-full bg-gradient-to-r from-gray-500 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            Re-login
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const ComprehensiveDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyId, setCompanyId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalCustomers: 0,
    totalLeads: 0,
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalDepartments: 0,
    totalLeaves: 0,
    totalHolidays: 0,
    attendanceRate: 0,
    performanceScore: 0,
    employeeTrend: 0,
    customerTrend: 0,
    projectTrend: 0,
    attendanceTrend: 0
  });
  const [dashboardData, setDashboardData] = useState({
    attendanceData: [],
    performanceData: [],
    projectStatus: [],
    departmentStats: [],
    todayAttendance: { present: 0, absent: 0, late: 0, holidays: 0, leaves: 0 },
    upcomingDeadlines: [],
    customerData: [],
    leadData: [],
    leaveData: [],
    holidayData: [],
    businessProfiles: []
  });

  // Get company ID from token
  const getCompanyIdFromToken = (decodedToken) => {
    let id = null;
    if (decodedToken?.role === 'company') {
      id = decodedToken.companyId || decodedToken.id;
    } else if (decodedToken?.role === 'employee' || decodedToken?.role === 'Employee') {
      id = decodedToken.companyId || decodedToken.company?._id || decodedToken.company;
    } else {
      id = decodedToken?.companyId || decodedToken?.company?._id || decodedToken?.id || null;
    }

    // Ensure ID is a string if it's an object
    if (id && typeof id === 'object') {
      id = id._id || id.id;
    }

    if (id) localStorage.setItem('selectedCompanyId', id);
    return id;
  };

  // Enhanced fetch helper with proper endpoint handling
  const fetchWithAuth = async (endpoint, options = {}) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found");

      const url = `${API_BASE_URL}${endpoint}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} for ${url}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      return [];
    }
  };

  // Fetch company-specific data with correct endpoints
  const fetchCompanyData = async (companyId) => {
    if (!companyId) throw new Error("Company ID is required");

    try {
      const [
        employeesResponse,
        projectsResponse,
        attendanceResponse,
        tasksResponse,
        departmentsResponse,
        holidaysResponse,
        leavesResponse,
        customersResponse,
        leadsResponse,
        businessProfilesResponse,
        rolesResponse,
        todosResponse,
        companyDetailsResponse
      ] = await Promise.allSettled([
        fetchWithAuth(`/api/employees/company/${companyId}`),
        fetchWithAuth(`/api/projectsmgnt`),
        fetchWithAuth(`/api/attendance?company=${companyId}`),
        fetchWithAuth(`/api/task/tasks`),
        fetchWithAuth(`/api/departments/company/${companyId}`),
        fetchWithAuth(`/api/holidays/company/${companyId}`),
        fetchWithAuth(`/api/leaves?company=${companyId}`),
        fetchWithAuth(`/api/customers`),
        LeadsService.getAllLeads(),
        fetchWithAuth(`/api/business-profiles`),
        fetchWithAuth(`/api/role`),
        fetchWithAuth(`/api/todos`),
        fetchWithAuth(`/api/companies/${companyId}`)
      ]);

      const extractData = (result, dataKey = null) => {
        if (result.status === 'rejected') return [];
        const data = result.value;

        console.log('🔍 Extract Data - Input:', { dataKey, data });

        if (Array.isArray(data)) return data;
        else if (dataKey && data[dataKey] && Array.isArray(data[dataKey])) return data[dataKey];
        else if (data.data && Array.isArray(data.data)) return data.data;
        else if (data.employees && Array.isArray(data.employees)) return data.employees;
        else if (data.projects && Array.isArray(data.projects)) return data.projects;
        else if (data.attendance && Array.isArray(data.attendance)) return data.attendance;
        else if (data.tasks && Array.isArray(data.tasks)) return data.tasks;
        else if (data.departments && Array.isArray(data.departments)) return data.departments;
        else if (data.holidays && Array.isArray(data.holidays)) return data.holidays;
        else if (data.leaves && Array.isArray(data.leaves)) return data.leaves;
        else if (data.customers && Array.isArray(data.customers)) return data.customers;
        else if (data.leads && Array.isArray(data.leads)) return data.leads;
        else if (data.businessProfiles && Array.isArray(data.businessProfiles)) return data.businessProfiles;
        else if (data.roles && Array.isArray(data.roles)) return data.roles;
        else if (data.todos && Array.isArray(data.todos)) return data.todos;
        else return [];
      };

      const rawData = {
        employees: extractData(employeesResponse, 'employees'),
        projects: extractData(projectsResponse, 'projects'),
        attendance: extractData(attendanceResponse, 'attendance'),
        tasks: extractData(tasksResponse, 'tasks'),
        departments: extractData(departmentsResponse, 'departments'),
        holidays: extractData(holidaysResponse, 'holidays'),
        leaves: extractData(leavesResponse, 'leaves'),
        customers: extractData(customersResponse, 'customers'),
        leads: extractData(leadsResponse, 'leads'),
        businessProfiles: extractData(businessProfilesResponse, 'businessProfiles'),
        roles: extractData(rolesResponse, 'roles'),
        todos: extractData(todosResponse, 'todos'),
        company: companyDetailsResponse.status === 'fulfilled' ?
          (companyDetailsResponse.value.data || companyDetailsResponse.value.company || companyDetailsResponse.value) :
          {}
      };

      console.log('🔍 Leads Response Status:', leadsResponse.status);
      console.log('🔍 Leads Response Value:', leadsResponse.value);
      console.log('🔍 Raw Leads Data from API:', rawData.leads);
      console.log('📊 Leads Count:', rawData.leads.length);

      return rawData;
    } catch (error) {
      console.error('Error fetching company data:', error);
      throw error;
    }
  };

  // Fetch employee-specific data
  const fetchEmployeeData = async (employeeId) => {
    if (!employeeId) throw new Error("Employee ID is required");

    try {
      // Use correct query params: assignedTo for tasks, employee for attendance
      // Projects fetched without filter so we can filter client-side by teamMembers
      const [tasksResponse, attendanceResponse, projectsResponse, leadsResponse] = await Promise.allSettled([
        fetchWithAuth(`/api/task/tasks?assignedTo=${employeeId}`),
        fetchWithAuth(`/api/attendance?employee=${employeeId}`),
        fetchWithAuth(`/api/projectsmgnt`),
        LeadsService.getAllLeads()
      ]);

      const extractData = (result, keys = []) => {
        if (result.status === 'rejected') return [];
        const data = result.value;
        if (Array.isArray(data)) return data;
        for (const key of keys) {
          if (data[key] && Array.isArray(data[key])) return data[key];
        }
        if (data.data && Array.isArray(data.data)) return data.data;
        return [];
      };

      return {
        tasks: extractData(tasksResponse, ['tasks']),
        attendance: extractData(attendanceResponse, ['records', 'attendance']),
        projects: extractData(projectsResponse, ['projects']),
        leads: leadsResponse.status === 'fulfilled' && leadsResponse.value.success ? leadsResponse.value.data : []
      };
    } catch (error) {
      console.error('Error fetching employee data:', error);
      throw error;
    }
  };

  // Process dashboard data for company
  const processDashboardData = (rawData, companyId) => {
    const {
      employees = [],
      projects = [],
      attendance = [],
      tasks = [],
      departments = [],
      holidays = [],
      leaves = [],
      customers = [],
      leads = [],
      businessProfiles = [],
      roles = [],
      todos = [],
      company = {}
    } = rawData;

    const filterByCompany = (items, companyField = 'company') => {
      return items.filter(item =>
        item[companyField] === companyId ||
        (item[companyField]?._id === companyId) ||
        !item[companyField]
      );
    };

    const safeEmployees = filterByCompany(employees);
    const safeProjects = filterByCompany(projects);
    const safeAttendance = filterByCompany(attendance);
    const safeTasks = filterByCompany(tasks).filter(task => {
      const isParentTemplate = task.repeat && !task.isRecurringInstance;
      return !isParentTemplate;
    });
    const safeDepartments = filterByCompany(departments);
    const safeHolidays = filterByCompany(holidays);
    const safeLeaves = filterByCompany(leaves);
    const safeCustomers = filterByCompany(customers);
    // Leads are already filtered by company in the backend via workflow
    const safeLeads = leads;
    const safeBusinessProfiles = filterByCompany(businessProfiles);
    const safeRoles = filterByCompany(roles);
    const safeTodos = filterByCompany(todos, 'userId');

    // Calculate stats
    const totalEmployees = safeEmployees.length;
    const totalCustomers = safeCustomers.length;
    const totalLeads = safeLeads.length;

    console.log('✅ Final Leads Count:', totalLeads);
    console.log('✅ Final Leads Data:', safeLeads);
    const totalProjects = safeProjects.length;
    const completedProjects = safeProjects.filter(p => ['Completed', 'completed', 'Comleted', 'Done', 'done'].includes(p.status)).length;
    const activeProjects = totalProjects - completedProjects;
    const totalDepartments = safeDepartments.length;
    const totalLeaves = safeLeaves.filter(l => ['pending', 'approved', 'Pending', 'Approved'].includes(l.status?.toLowerCase())).length;
    const totalHolidays = safeHolidays.length;

    const attendanceByDay = processAttendanceData(safeAttendance, safeHolidays, safeLeaves, company);
    const performanceData = processPerformanceData(safeEmployees, safeTasks);
    const projectStatus = processProjectStatus(safeProjects);
    const departmentStats = processDepartmentStats(safeDepartments, safeEmployees, safeTasks);
    const todayAttendance = calculateTodayAttendance(safeAttendance, safeHolidays, safeLeaves, company);
    const upcomingDeadlines = getUpcomingDeadlines(safeProjects);

    const attendanceRate = safeAttendance.length > 0
      ? Math.round((safeAttendance.filter(a => a.status === 'present' || a.status === 'Present' || (a.inTime && a.outTime)).length / safeAttendance.length) * 100)
      : 0;
    const performanceScore = performanceData.length > 0
      ? Math.round(performanceData.reduce((sum, emp) => sum + (emp.rate || 0), 0) / performanceData.length)
      : 0;

    // Calculate trends (compare with last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Last month's data
    const lastMonthEmployees = safeEmployees.filter(e => new Date(e.createdAt || e.dateOfJoining) < thirtyDaysAgo).length;
    const lastMonthCustomers = safeCustomers.filter(c => new Date(c.createdAt) < thirtyDaysAgo).length;
    const lastMonthProjects = safeProjects.filter(p => new Date(p.createdAt || p.startDate) < thirtyDaysAgo && ['Inprogress', 'In Progress', 'perogress', 'Planning', 'Start'].includes(p.status)).length;
    const lastMonthAttendance = safeAttendance.filter(a => new Date(a.date || a.createdAt) < thirtyDaysAgo);
    const lastMonthAttendanceRate = lastMonthAttendance.length > 0
      ? Math.round((lastMonthAttendance.filter(a => a.status === 'present' || a.status === 'Present' || (a.inTime && a.outTime)).length / lastMonthAttendance.length) * 100)
      : 0;

    // Calculate percentage changes
    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return Math.round(((current - previous) / previous) * 100);
    };

    const employeeTrend = calculateChange(totalEmployees, lastMonthEmployees);
    const customerTrend = calculateChange(totalCustomers, lastMonthCustomers);
    const projectTrend = calculateChange(safeProjects.filter(p => ['Inprogress', 'In Progress', 'perogress', 'Planning', 'Start'].includes(p.status)).length, lastMonthProjects);
    const attendanceTrend = attendanceRate - lastMonthAttendanceRate;

    return {
      stats: {
        totalEmployees,
        totalCustomers,
        totalLeads,
        totalProjects,
        activeProjects,
        completedProjects,
        totalDepartments,
        totalLeaves,
        totalHolidays,
        attendanceRate,
        performanceScore,
        // Add trend data
        employeeTrend,
        customerTrend,
        projectTrend,
        attendanceTrend
      },
      dashboardData: {
        attendanceData: attendanceByDay,
        performanceData,
        projectStatus,
        departmentStats,
        todayAttendance,
        upcomingDeadlines,
        customerData: safeCustomers,
        leadData: safeLeads,
        leaveData: safeLeaves,
        holidayData: safeHolidays,
        businessProfiles: safeBusinessProfiles
      }
    };
  };

  // Process employee dashboard data scoped to the logged-in employee
  const processEmployeeDashboardData = (rawData, employeeId) => {
    const { projects = [], attendance = [], leads = [] } = rawData;
    const tasks = (rawData.tasks || []).filter(task => {
      const isParentTemplate = task.repeat && !task.isRecurringInstance;
      return !isParentTemplate;
    });

    // Filter projects where this employee is a team member or project head
    const employeeIdStr = String(employeeId);
    const employeeProjects = projects.filter(p => {
      const members = p.teamMembers || [];
      const isTeamMember = members.some(m =>
        String(m) === employeeIdStr || String(m?._id) === employeeIdStr
      );
      const isHead = String(p.projectHead) === employeeIdStr ||
        String(p.projectHead?._id) === employeeIdStr;
      return isTeamMember || isHead;
    });

    // Task stats — tasks already filtered server-side to this employee via ?assignedTo=
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t =>
      ['Completed', 'completed', 'Comleted', 'Done', 'done', 'Closed', 'closed',
        'complete', 'late-complete', 're-late-complete'].includes(t.status)
    ).length;
    const pendingTasks = tasks.filter(t =>
      ['pending', 'Pending'].includes(t.status)
    ).length;
    const overdueTasks = tasks.filter(t =>
      t.status && t.status.toLowerCase().includes('overdue')
    ).length;

    // Project stats
    const totalProjects = employeeProjects.length;
    const completedProjects = employeeProjects.filter(p =>
      ['Completed', 'completed', 'Comleted', 'Done', 'done'].includes(p.status)
    ).length;
    const activeProjects = totalProjects - completedProjects;

    // Attendance rate from employee's own records
    const attendanceRate = attendance.length > 0
      ? Math.round((attendance.filter(a =>
        a.status === 'present' || a.status === 'Present' || (a.inTime && a.outTime)
      ).length / attendance.length) * 100)
      : 0;

    const performanceScore = totalTasks > 0
      ? Math.round((completedTasks / totalTasks) * 100)
      : 0;

    // Reuse the same stat field names as company dashboard for sub-component compatibility
    // totalEmployees -> My Total Tasks | totalCustomers -> Tasks Completed
    const stats = {
      totalEmployees: totalTasks,
      totalCustomers: completedTasks,
      totalLeads: leads.length,
      totalProjects,
      activeProjects,
      completedProjects,
      totalDepartments: overdueTasks,
      totalLeaves: 0,
      totalHolidays: 0,
      attendanceRate,
      performanceScore,
      employeeTrend: 0,
      customerTrend: 0,
      projectTrend: 0,
      attendanceTrend: 0
    };

    // Build dashboardData with employee-specific details
    const projectStatus = processProjectStatus(employeeProjects);
    const upcomingDeadlines = getUpcomingDeadlines(employeeProjects);
    const attendanceData = processAttendanceData(attendance, [], [], {});
    const todayAttendance = calculateTodayAttendance(attendance, [], [], {});

    const dashboardData = {
      attendanceData,
      performanceData: [],
      projectStatus,
      departmentStats: [],
      todayAttendance,
      upcomingDeadlines,
      customerData: [],
      leadData: leads,
      leaveData: [],
      holidayData: [],
      businessProfiles: []
    };

    return { stats, dashboardData };
  };

  // Data processing functions (same as your existing logic)
  const processAttendanceData = (attendance, holidays, leaves, company) => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split('T')[0];

      // Count attendance for this specific date
      const dayAttendance = attendance.filter(a => {
        const attDate = new Date(a.date || a.createdAt);
        return attDate.toISOString().split('T')[0] === dateString;
      });

      const present = dayAttendance.filter(a =>
        a.status === 'present' || a.status === 'Present' || (a.inTime && a.outTime)
      ).length;

      const absent = dayAttendance.filter(a =>
        a.status === 'absent' || a.status === 'Absent'
      ).length;

      const late = dayAttendance.filter(a =>
        a.status === 'late' || a.status === 'Late' || a.isLate === true
      ).length;

      const holidayCount = holidays.filter(h => {
        const holidayDate = new Date(h.date);
        return holidayDate.toISOString().split('T')[0] === dateString;
      }).length;

      const leaveCount = leaves.filter(l => {
        const fromDate = new Date(l.fromDate);
        const toDate = new Date(l.toDate);
        return date >= fromDate && date <= toDate &&
          (l.status === 'approved' || l.status === 'Approved');
      }).length;

      return {
        name: date.toLocaleDateString('en-US', { weekday: 'short' }),
        present,
        absent,
        late,
        holiday: holidayCount,
        leave: leaveCount
      };
    }).reverse();

    return last7Days;
  };

  const processPerformanceData = (employees, tasks) => {
    console.log('🔍 Processing Performance Data:', {
      totalEmployees: employees.length,
      totalTasks: tasks.length,
      sampleTask: tasks[0]
    });

    // Calculate real performance metrics for each employee
    const employeePerformance = employees.map(employee => {
      const empId = employee._id || employee.id;

      // Filter tasks assigned to this employee (handle both single ID and array of IDs)
      const empTasks = tasks.filter(task => {
        // Handle assignedTo as array
        if (Array.isArray(task.assignedTo)) {
          return task.assignedTo.includes(empId) ||
            task.assignedTo.some(id => id?._id === empId || id === empId);
        }
        // Handle assignedTo as single value
        return task.assignedTo === empId ||
          task.assignedTo?._id === empId ||
          task.employee === empId ||
          task.employee?._id === empId;
      });

      const totalTasks = empTasks.length;
      const completedTasks = empTasks.filter(t =>
        ['Completed', 'completed', 'Done', 'done', 'Closed', 'closed', 'complete', 'late-complete', 're-late-complete'].includes(t.status)
      ).length;

      const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      const points = completedTasks * 10; // 10 points per completed task

      if (totalTasks > 0) {
        console.log(`👤 Employee: ${employee.teamMemberName || employee.name}`, {
          empId,
          totalTasks,
          completedTasks,
          completionRate,
          points
        });
      }

      return {
        name: employee.teamMemberName || employee.name || 'Unknown',
        points: points,
        completed: completedTasks,
        rate: completionRate,
        role: employee.role || employee.designation || 'Employee',
        totalTasks: totalTasks
      };
    });

    // Sort by completed tasks count first, then by completion rate
    const topPerformers = employeePerformance
      .filter(emp => emp.totalTasks > 0) // Only include employees with tasks
      .sort((a, b) => {
        // Sort by completed tasks count (descending)
        if (b.completed !== a.completed) return b.completed - a.completed;
        // If completed tasks are equal, sort by completion rate
        return b.rate - a.rate;
      })
      .slice(0, 5);

    console.log('📊 Top Performers (sorted by completed tasks):', topPerformers);
    return topPerformers;
  };

  const processProjectStatus = (projects) => {
    const statusCount = {
      'Completed': 0,
      'Inprogress': 0,
      'Planning': 0,
      'On Hold': 0,
      'Start': 0,
      'perogress': 0
    };

    projects.forEach(project => {
      const status = project.status || 'Planning';
      // Normalize status variations
      if (status === 'In Progress' || status === 'in progress' || status === 'perogress') {
        statusCount['Inprogress'] = (statusCount['Inprogress'] || 0) + 1;
      } else if (status === 'Comleted' || status === 'completed' || status === 'done' || status === 'Done') {
        statusCount['Completed'] = (statusCount['Completed'] || 0) + 1;
      } else {
        statusCount[status] = (statusCount[status] || 0) + 1;
      }
    });

    // Filter out zero values and return
    return Object.entries(statusCount)
      .filter(([name, value]) => value > 0)
      .map(([name, value]) => ({
        name,
        value,
        color: {
          'Completed': '#10b981',
          'Inprogress': '#3b82f6',
          'In Progress': '#3b82f6',
          'perogress': '#3b82f6',
          'Planning': '#f59e0b',
          'On Hold': '#ef4444',
          'Start': '#6366f1',
          'Comleted': '#10b981'
        }[name] || '#6b7280'
      }));
  };

  const processDepartmentStats = (departments, employees, tasks) => {
    console.log('Processing Department Stats based on Employee Performance:', {
      departmentsCount: departments.length,
      employeesCount: employees.length,
      tasksCount: tasks.length
    });

    const deptStats = departments.slice(0, 5).map(dept => {
      const deptId = dept._id || dept.id;
      const deptName = dept.name || dept.departmentName;

      // Get employees in this department
      const deptEmployees = employees.filter(emp =>
        emp.department === deptId ||
        emp.department === deptName ||
        emp.department?._id === deptId
      );

      // Calculate department performance based on employee task completion
      let totalTasks = 0;
      let completedTasks = 0;

      deptEmployees.forEach(employee => {
        const empId = employee._id || employee.id;

        // Filter tasks assigned to this employee
        const empTasks = tasks.filter(task => {
          if (Array.isArray(task.assignedTo)) {
            return task.assignedTo.includes(empId) ||
              task.assignedTo.some(id => id?._id === empId || id === empId);
          }
          return task.assignedTo === empId ||
            task.assignedTo?._id === empId ||
            task.employee === empId ||
            task.employee?._id === empId;
        });

        totalTasks += empTasks.length;
        completedTasks += empTasks.filter(t =>
          ['Completed', 'completed', 'Done', 'done', 'Closed', 'closed', 'complete', 'late-complete', 're-late-complete'].includes(t.status)
        ).length;
      });

      // Calculate performance percentage based on task completion
      const performance = totalTasks > 0
        ? Math.round((completedTasks / totalTasks) * 100)
        : 0;

      return {
        name: deptName || 'Unknown',
        employees: deptEmployees.length,
        totalTasks: totalTasks,
        completedTasks: completedTasks,
        performance: performance
      };
    });

    console.log('📈 Department Stats (based on employee task completion):', deptStats);
    return deptStats;
  };

  const calculateTodayAttendance = (attendance, holidays, leaves, company) => {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];

    // Filter attendance for today
    const todayAttendance = attendance.filter(a => {
      const attDate = new Date(a.date || a.createdAt);
      return attDate.toISOString().split('T')[0] === todayString;
    });

    const present = todayAttendance.filter(a =>
      a.status === 'present' || a.status === 'Present' || (a.inTime && a.outTime)
    ).length;

    const absent = todayAttendance.filter(a =>
      a.status === 'absent' || a.status === 'Absent'
    ).length;

    const late = todayAttendance.filter(a =>
      a.status === 'late' || a.status === 'Late' || a.isLate === true
    ).length;

    const todayHolidays = holidays.filter(h => {
      const holidayDate = new Date(h.date);
      return holidayDate.toISOString().split('T')[0] === todayString;
    }).length;

    const todayLeaves = leaves.filter(l => {
      const fromDate = new Date(l.fromDate);
      const toDate = new Date(l.toDate);
      return today >= fromDate && today <= toDate &&
        (l.status === 'approved' || l.status === 'Approved');
    }).length;

    return {
      present,
      absent,
      late,
      holidays: todayHolidays,
      leaves: todayLeaves
    };
  };

  const getUpcomingDeadlines = (projects) => {
    return projects
      .filter(project => project.dueDate)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 5)
      .map(project => ({
        ...project,
        type: 'Project',
        title: project.title || project.name,
        dueDate: project.dueDate
      }));
  };

  // Fetch dashboard data effect
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Please log in to access the dashboard");

        const decodedToken = jwtDecode(token);
        const role = decodedToken.role;
        setUserRole(role);
        setUserId(decodedToken.id);

        let companyIdFromToken = getCompanyIdFromToken(decodedToken);
        setCompanyId(companyIdFromToken);

        if (role === 'company') {
          const rawData = await fetchCompanyData(companyIdFromToken);
          const processedData = processDashboardData(rawData, companyIdFromToken);
          console.log('✅ Setting Dashboard Data:', {
            performanceData: processedData.dashboardData.performanceData,
            departmentStats: processedData.dashboardData.departmentStats
          });
          setStats(processedData.stats);
          setDashboardData(processedData.dashboardData);
        } else {
          // Employee or any non-company role — scope data to the logged-in employee
          const employeeSpecificData = await fetchEmployeeData(decodedToken.id);
          const processedData = processEmployeeDashboardData(employeeSpecificData, decodedToken.id);
          setStats(processedData.stats);
          setDashboardData(processedData.dashboardData);
        }
      } catch (err) {
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Error UI
  if (error) {
    return <AnimatedError error={error} onRetry={() => window.location.reload()} />;
  }

  // Loading UI
  if (loading) {
    return <AnimatedLoading />;
  }

  // Tabs data filtered by role
  const tabs = [
    { id: 'overview', name: 'Overview', icon: <TrendingUp className="w-5 h-5" /> },
    { id: 'tasks', name: 'Tasks', icon: <Award className="w-5 h-5" /> },
    { id: 'leads', name: 'Leads', icon: <Target className="w-5 h-5" /> },
    { id: 'projects', name: 'Projects', icon: <FolderOpen className="w-5 h-5" /> },
    { id: 'attendance', name: 'Attendance', icon: <Calendar className="w-5 h-5" /> },
  ];

  if (userRole === 'company') {
    tabs.push({ id: 'branches', name: 'Branches', icon: <GitBranch className="w-5 h-5" /> });
  }

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">

      {/* Enhanced Header - Desktop view (md and up) */}
      <div className="hidden md:block relative bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-full mx-auto px-4 sm:px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Business Intelligence
                </h1>
                <p className="text-gray-600 flex items-center">
                  <Zap className="w-4 h-4 text-yellow-500 mr-1" />
                  Real-time organizational analytics
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {companyId && (
                <div className="text-right">
                  <p className="text-xs text-gray-500 flex items-center">
                    <Globe className="w-3 h-3 mr-1" />
                    Live data sync
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>



      {/* Navigation Tabs */}
      <div className="relative bg-slate-50/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-full mx-auto px-3 sm:px-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:flex md:flex-wrap md:justify-start gap-2 py-3">
            {tabs.map((tab) => (
              <GlowingTab
                key={tab.id}
                id={tab.id}
                name={tab.name}
                icon={tab.icon}
                isActive={activeTab === tab.id}
                onClick={setActiveTab}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Stats Overview - uncomment if wanted */}
      {/* <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnimatedStatCard
            icon={<Users className="w-6 h-6 text-white" />}
            title="Total Employees"
            value={stats.totalEmployees}
            change={12}
            color="from-blue-500 to-blue-600"
            delay={100}
          />
          <AnimatedStatCard
            icon={<Target className="w-6 h-6 text-white" />}
            title="Active Projects"
            value={stats.activeProjects}
            change={8}
            color="from-green-500 to-green-600"
            delay={200}
          />
          <AnimatedStatCard
            icon={<BarChart3 className="w-6 h-6 text-white" />}
            title="Performance Score"
            value={`${stats.performanceScore}%`}
            change={5}
            color="from-purple-500 to-purple-600"
            delay={300}
          />
          <AnimatedStatCard
            icon={<CheckCircle2 className="w-6 h-6 text-white" />}
            title="Attendance Rate"
            value={`${stats.attendanceRate}%`}
            change={3}
            color="from-orange-500 to-orange-600"
            delay={400}
          />
        </div>
      </div> */}

      {/* Main Content */}
      <div className="relative max-w-full mx-auto px-4 py-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden">
          {activeTab === 'overview' && <OverviewDashboard stats={stats} dashboardData={dashboardData} userRole={userRole} />}
          {activeTab === 'attendance' && <AttendanceDashboard stats={stats} dashboardData={dashboardData} />}
          {activeTab === 'tasks' && <TasksDashboard stats={stats} dashboardData={dashboardData} />}
          {activeTab === 'projects' && <ProjectsDashboard stats={stats} dashboardData={dashboardData} />}
          {activeTab === 'leads' && <LeadsDashboard stats={stats} dashboardData={dashboardData} />}
          {activeTab === 'branches' && userRole === 'company' && <BranchesDashboard stats={stats} dashboardData={dashboardData} />}
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveDashboard;

